import { onMounted, onUnmounted, nextTick, computed, reactive } from 'vue'
import { getKey } from '@haohan/clw-utils'
import { useWidget, useProject } from '@haohan/clw-store'
import type { WidgetDataInfo } from '../../types/commons'
import { ElMessage } from 'element-plus'
import { transOption2TypeObj } from '../utils/system-tools'
import { cloneDeep } from 'lodash-es'

const allComps = transOption2TypeObj()
let copyWidget: any = { type: 'Blank' }
const copyFromInfo = reactive({
  projectId: '', // 复制的项目，默认当前项目
  pageType: '' // 复制的页面类型，默认当前页面类型
})

function getCompDefaultSetting(comp: Widget, hasData: boolean) {
  const { dataProp, events, type, ...rest } = comp
  const compSetting = allComps[type]
  const key = getKey()
  if (hasData) {
    return cloneDeep({ ...compSetting, ...rest, dataProp, key })
  }
  return cloneDeep({ ...compSetting, ...rest, key })
}

/**
 * 复制粘贴功能
 */
export function useCopyAndPaste(widgetData: WidgetDataInfo) {
  const widgetStore = useWidget()
  const projectStore = useProject()
  const cpid = computed(() => projectStore.currentProject?.id)
  const cpageType = computed(() => projectStore.currentPage?.variables?.pageType || 'normal')

  /**
   * 复制store中选中得组件
   */
  const copyWidgetStoreItem = (): void => {
    copyItem(widgetStore.selectWidget)
  }
  /**
   * 复制组件
   * @param ele 需要复制得组件
   */
  const copyItem = (ele: Widget, opt?: any): void => {
    copyFromInfo.projectId = opt?.pid || cpid.value
    copyFromInfo.pageType = opt?.pageType || cpageType.value
    if (widgetStore.selectWidget.type !== 'Blank') {
      copyWidget = cloneDeep(ele)
      // 大屏才有 top, 解决粘贴后重叠在一起看不出是否成功的问题
      if (copyWidget.styleProp?.top || copyWidget.styleProp?.top === 0) {
        copyWidget.styleProp.top += 40
      }
      ElMessage.info('复制成功')
    } else {
      ElMessage.warning('请先选中需要复制的组件')
    }
  }
  /**
   * 粘贴组件
   * 普通组件
   */
  const pasteItem = (opt?: any) => {
    const pid = opt?.pid || cpid.value
    const pageType = opt?.pageType || cpageType.value
    if (pageType !== copyFromInfo.pageType) {
      ElMessage.warning('请在同类型页面中进行粘贴组件')
      return
    }
    // 如果在打开时，不复制组件，则默认使用当前选中得组件进行粘贴
    if (copyWidget.type === 'Blank' && widgetStore.selectWidget.type !== 'Blank') {
      copyWidget = JSON.parse(JSON.stringify(widgetStore.selectWidget))
    } else if (copyWidget.type === 'Blank') {
      ElMessage.warning('未复制组件')
      return
    }
    const newWidget = getCompDefaultSetting(copyWidget, pid === copyFromInfo.projectId)

    if (newWidget.type === 'HHGrid') {
      // 更新容器中得组件Key
      updateGridKey(newWidget)
    }
    if (newWidget.type === 'HHTabPaneWidget') {
      const optionsAttr = newWidget.dataProp?.defaultData
      // 更新容器中得组件Key
      updateTabPaneKey(optionsAttr)
    }
    if (widgetStore.selectWidget.type === 'HHGrid') {
      widgetStore.selectWidget.children![0].list?.push(newWidget)
    } else {
      widgetData.list.push(newWidget)
      nextTick(() => {
        const box = document.querySelector('#preview .view-box')
        // console.log(box)
        box?.scrollTo({
          top: box.clientHeight < 10000 ? 10000 : box.clientHeight + 1000,
          left: 0,
          behavior: 'smooth'
        })
      })
    }
    // 选中粘贴的组件
    widgetStore.changeSelectWidget(newWidget)
  }
  /**
   * 粘贴组件
   */
  const pasteItemByBigScreen = (opt?: any) => {
    const pid = opt?.pid || cpid.value
    const pageType = opt?.pageType || cpageType.value
    if (pageType !== copyFromInfo.pageType) {
      ElMessage.warning('请在同类型页面中进行粘贴组件')
      return false
    }
    // 如果在打开时，不复制组件，则默认使用当前选中得组件进行粘贴
    if (copyWidget.type === 'Blank' && widgetStore.selectWidget.type !== 'Blank') {
      copyWidget = JSON.parse(JSON.stringify(widgetStore.selectWidget))
    } else if (copyWidget.type === 'Blank') {
      ElMessage.warning('请先选中需要复制得组件')
      return
    }

    let newWidget = null
    if (copyWidget.type === 'Group') {
      newWidget = JSON.parse(JSON.stringify(copyWidget))
      newWidget.key = getKey()
    } else {
      newWidget = getCompDefaultSetting(copyWidget, pid === copyFromInfo.projectId)
    }

    // 大屏组合组件复制, 需要更新每个组件的 key
    if (newWidget.type === 'Group' && newWidget?.groupComponents?.length) {
      newWidget?.groupComponents.forEach((item: Widget) => {
        item.key = getKey()
        item.events = []
      })
    }
    if (widgetStore.widgetDataList.length > 0) {
      newWidget.styleProp['z-index'] = widgetStore.widgetDataList[0].styleProp['z-index'] + 1
    }
    widgetData.list.push(newWidget)
    // 选中粘贴的组件
    widgetStore.changeSelectWidget(newWidget)
  }

  onMounted(() => {
    document.addEventListener('keydown', keydownFn)
  })
  onUnmounted(() => {
    document.removeEventListener('keydown', keydownFn)
  })
  // 监听ctrl+c，ctrl+v
  function keydownFn(e: any) {
    const { ctrlKey, keyCode, target } = e
    if (ctrlKey && (target.tagName === 'BODY' || target.closest('#preview .view-box'))) {
      // console.log('keydown', e)
      if (keyCode === 67) {
        copyItem(widgetStore.selectWidget)
      } else if (keyCode === 86) {
        if (cpageType.value === 'bigScreen') {
          pasteItemByBigScreen()
        } else {
          pasteItem()
        }
      }
    }
  }
  // 更新容器key
  const updateGridKey = (widget: Widget): void => {
    widget.children!.forEach((c) => {
      c.list?.forEach((cl) => {
        cl.key = getKey()
        cl.events = []
        if (cl.type === 'HHGrid') {
          updateGridKey(cl)
        } else if (cl.type === 'HHTabPaneWidget') {
          const optionsAttr = cl.dataProp?.defaultData
          if (optionsAttr) {
            updateTabPaneKey(optionsAttr)
          }
        }
      })
    })
  }
  // 更新面板中得组件key
  const updateTabPaneKey = (widget: any[]): void => {
    widget.forEach((w) => {
      w.list.forEach((wl: Widget) => {
        wl.key = getKey()
        wl.events = []
        if (wl.type === 'HHGrid') {
          updateGridKey(wl)
        } else if (wl.type === 'HHTabPaneWidget') {
          const optionsAttr = wl.dataProp?.defaultData
          if (optionsAttr) {
            updateTabPaneKey(optionsAttr)
          }
        }
      })
    })
  }
  return { copyWidgetStoreItem, copyItem, pasteItem, pasteItemByBigScreen }
}
